import { login } from "api/auth";
import { ApiError } from "api/client";
import { Form, Formik, type FormikHelpers } from "formik";
import AccountsContainer from "pages/accounts-onboarding/components/accounts-container";
import type { LoginFormModel } from "pages/accounts-onboarding/models";
import { createLoginFormSchema } from "pages/accounts-onboarding/schema";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getPostSignInPath } from "routes/signInRedirect";
import { useSession } from "Session";

import LoginForm from "./components";

type LoginPageProps = {
	initialValues: LoginFormModel;
};

export const LoginPage = ({ initialValues }: LoginPageProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { signIn } = useSession();
	const { t } = useTranslation("accountsPage");
	const validationSchema = useMemo(() => createLoginFormSchema(t), [t]);

	const onFormSubmit = useCallback(
		async (values: LoginFormModel, formikHelpers: FormikHelpers<LoginFormModel>): Promise<void> => {
			try {
				const auth = await login({ email: values.email, password: values.password });
				signIn(auth);
				navigate(getPostSignInPath(location.search), { replace: true });
			} catch (error) {
				formikHelpers.setStatus(error instanceof ApiError ? error.message : t("login.error"));
				formikHelpers.setSubmitting(false);
			}
		},
		[location.search, navigate, signIn, t]
	);

	return (
		<Formik initialValues={initialValues} onSubmit={onFormSubmit} validationSchema={validationSchema}>
			<Form noValidate>
				<AccountsContainer>
					<LoginForm />
				</AccountsContainer>
			</Form>
		</Formik>
	);
};
